import { useState } from "react"
import { IconLifeRingFill18 as LifeRing } from "nucleo-ui-fill-18"
import Balancer from "react-wrap-balancer"

import { cn } from "@productlane/lib/src/browser"
import { Button, Switch } from "@productlane/ui"

import { toast } from "@/components/toast"
import { analytics } from "@/lib/analytics"
import { api } from "@/lib/api"
import { useSession } from "@/lib/session"
import {
	ChangelogIcon,
	DocsIcon,
	InboxNavIcon,
	RequestsIcon,
} from "@/screens/app/nav-icons"

interface UseCase {
	id:
		| "customer-support"
		| "customer-requests-portal"
		| "support-portal"
		| "changelog"
		| "docs"
	title: string
	description: string
	icon: React.ElementType
}

// Descriptions stay short enough to hold one line at phone width.
const USE_CASES: UseCase[] = [
	{
		id: "customer-support",
		title: "Customer support",
		description: "Handle inquiries and resolve issues",
		icon: InboxNavIcon,
	},
	{
		id: "docs",
		title: "Help center",
		description: "Self-updating from MCP or GitHub",
		icon: DocsIcon,
	},
	{
		id: "customer-requests-portal",
		title: "Feature requests",
		description: "Linear requests your customers can see",
		icon: RequestsIcon,
	},
	{
		id: "support-portal",
		title: "Support portal",
		description: "Customers track their own threads",
		icon: LifeRing,
	},
	{
		id: "changelog",
		title: "Changelog",
		description: "Release notes from MCP or Linear",
		icon: ChangelogIcon,
	},
]

export function UseCasesSlide({ nextSlide }: { nextSlide: () => void }) {
	const { session } = useSession()

	const [selectedUseCases, setSelectedUseCases] = useState<Set<UseCase["id"]>>(
		() => new Set<UseCase["id"]>(["customer-support", "docs"]),
	)

	const updateWorkspaceMutation = api.workspace.update.useMutation()

	const toggleUseCase = (id: UseCase["id"]) => {
		setSelectedUseCases((prev) => {
			const next = new Set(prev)
			if (next.has(id)) {
				next.delete(id)
			} else {
				next.add(id)
			}
			return next
		})
	}

	const handleContinue = async () => {
		if (selectedUseCases.size === 0) {
			toast.info({
				title: "Selection required",
				description: "Please select at least one use case to continue.",
			})
			return
		}

		try {
			await updateWorkspaceMutation.mutateAsync({
				domainPublic: selectedUseCases.has("customer-requests-portal"),
				changelogPublic: selectedUseCases.has("changelog"),
				helpCenterPublic: selectedUseCases.has("docs"),
				...(selectedUseCases.has("support-portal") && {
					customerPortalSettings: {
						isEnabled: true,
					},
				}),
			})
		} catch (error) {
			toast.error({
				title: "Couldn't save your selection",
				description:
					error instanceof Error ? error.message : "Please try again.",
			})
			return
		}

		try {
			analytics.track("use-cases-selected", {
				userId: session?.userId,
				workspaceId: session?.workspaceId,
				useCases: Array.from(selectedUseCases),
			})
		} catch {
			// Never let analytics failures block onboarding progress.
		}

		nextSlide()
	}

	return (
		<div className="flex flex-col items-center text-center">
			<div className="mb-1 text-3xl font-bold tracking-tight">
				Active features
			</div>
			<Balancer className="tracking-tight text-secondary">
				You can change this later in the settings.
			</Balancer>

			<div className="mt-8 w-full max-w-lg border-t border-gray-200 text-left dark:border-gray-700/80">
				{USE_CASES.map((useCase) => {
					const Icon = useCase.icon
					const isSelected = selectedUseCases.has(useCase.id)

					return (
						<div
							key={useCase.id}
							className={cn(
								"cursor-default select-none border-b border-gray-200 dark:border-gray-700/80",
								"hover:bg-gray-50 dark:hover:bg-gray-800/40",
							)}
						>
							<Switch
								name={useCase.id}
								checked={isSelected}
								onCheckedChange={() => toggleUseCase(useCase.id)}
								className="px-1.5 py-3"
								switchClassName="gap-3"
								label={
									<span className="flex items-start gap-[14px]">
										<Icon
											className={cn(
												"mt-[1.5px] h-[17px] w-[17px] flex-none transform-gpu",
												isSelected ? "text-green-500" : "text-tertiary",
											)}
											strokeWidth={1.75}
										/>
										<span className="min-w-0 flex-1">
											<span className="block text-sm font-medium">
												{useCase.title}
											</span>
											<span className="block truncate text-xs font-normal text-secondary">
												{useCase.description}
											</span>
										</span>
									</span>
								}
							/>
						</div>
					)
				})}
			</div>

			<Button
				size="xl"
				className="mt-8 w-full max-w-xxs"
				onClick={handleContinue}
				isLoading={updateWorkspaceMutation.isLoading}
			>
				Continue
			</Button>
		</div>
	)
}
